import React, { useState } from "react";
import {
  FieldItem,
  GenericSelect,
  GenericSelectProps,
  Option,
  Text,
  useTheme,
  getFieldErrorMessage,
  useFieldContext,
  useFieldRegister,
  useFormSubscription
} from "@bps/fluent-ui";
import { useGateways } from "@libs/api/gateways-context";

type FormAdUserSearchProps = Omit<
  GenericSelectProps,
  | "theme"
  | "options"
  | "searchBoxProps"
  | "selectedKeys"
  | "onChangeSelectedKeys"
  | "label"
> & { name: string; label?: string };

export const FormAdUserSearch: React.FC<FormAdUserSearchProps> = ({
  name,
  disabled,
  label,
  required,
  ...props
}) => {
  const { envGateway } = useGateways();

  const theme = useTheme();

  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useFieldRegister(name);
  const { value, setFieldValue, meta, disabledForm } = useFieldContext<
    string | string[] | undefined
  >(name);

  const {
    state: { triedToSubmit }
  } = useFormSubscription({ subscriptions: { triedToSubmit: true } });

  const onSearch = async (value: string | undefined) => {
    if (!value) {
      return;
    }
    setLoading(true);
    const users = await envGateway.searchUsers(value);
    setOptions(
      users.length
        ? users.map(user => ({ key: user.id, id: user.id, text: user.name }))
        : []
    );
    setLoading(false);
  };

  const errorMessage = meta
    ? getFieldErrorMessage(meta, {
        triedToSubmit
      })
    : undefined;

  return (
    <FieldItem
      name={name}
      errorMessage={errorMessage}
      label={label}
      required={required}
    >
      <GenericSelect
        loading={loading}
        options={options}
        searchBoxProps={{
          onSearch
        }}
        selectedKeys={value || []}
        multiSelect={false}
        onChangeSelectedKeys={setFieldValue}
        disabled={disabledForm ?? disabled}
        styles={{
          field: {
            justifyContent: "space-between",
            borderColor: !!errorMessage
              ? theme.semanticColors.errorText
              : undefined
          }
        }}
        onRenderOption={(option: Option) => <Text>{option.text}</Text>}
        {...props}
      />
    </FieldItem>
  );
};
